// The schema and markdown round-trip behind WootWriter/Editor.vue, chosen per channel.
//
// Underline is email-only. Markdown has no underline, so the mark is stored as
// `++text++` (markdown-it-ins syntax): the backend ChatwootMarkdownRenderer turns it into <u> in
// the sent email and MessageFormatter does the same in the dashboard. Only email gets this schema:
// on a chat channel the `++` would reach the customer as literal text.
import MarkdownIt from 'markdown-it';
import markdownItIns from 'markdown-it-ins';
import { MarkdownParser, MarkdownSerializer } from 'prosemirror-markdown';
import { Schema } from 'prosemirror-model';
import { toggleMark } from 'prosemirror-commands';
import { keymap } from 'prosemirror-keymap';
import {
  messageSchema,
  MessageMarkdownSerializer,
  MessageMarkdownTransformer,
} from '@chatwoot/prosemirror-schema';
import {
  messageSchemaToMdMapping,
  messageMdToPmMapping,
} from '@chatwoot/prosemirror-schema/src/schema/markdown/messageParser';
import { filterMdToPmSchemaMapping } from '@chatwoot/prosemirror-schema/src/schema/markdown/parser';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const underlineMark = {
  parseDOM: [
    { tag: 'u' },
    { tag: 'ins' },
    {
      style: 'text-decoration',
      getAttrs: value => (value.includes('underline') ? null : false),
    },
  ],
  toDOM: () => ['u', 0],
};

// Before `code` so the serializer nests `++` outside backticks, where it still parses.
const underlineSchema = new Schema({
  nodes: messageSchema.spec.nodes,
  marks: messageSchema.spec.marks.addBefore('code', 'underline', underlineMark),
});

// Same tokenizer setup as the library's MessageMarkdownTransformer, plus `++`.
const tokenizer = MarkdownIt('commonmark', { html: false, linkify: false })
  .enable(['entity', 'escape'])
  .disable(['table', 'hr', 'heading', 'lheading'], true)
  .use(markdownItIns);
['nodes', 'marks'].forEach(key => {
  Object.entries(messageSchemaToMdMapping[key]).forEach(([name, rules]) => {
    if (underlineSchema[key][name]) tokenizer.enable(rules);
  });
});

const parser = new MarkdownParser(underlineSchema, tokenizer, {
  ...filterMdToPmSchemaMapping(underlineSchema, messageMdToPmMapping),
  ins: { mark: 'underline' },
});

const serializer = new MarkdownSerializer(MessageMarkdownSerializer.nodes, {
  ...MessageMarkdownSerializer.marks,
  underline: {
    open: '++',
    close: '++',
    mixable: true,
    expelEnclosingWhitespace: true,
  },
});

const emailMarkdown = {
  schema: underlineSchema,
  parse: content => parser.parse(content),
  serialize: doc => serializer.serialize(doc),
  plugins: () => [
    keymap({ 'Mod-u': toggleMark(underlineSchema.marks.underline) }),
  ],
  menuOptions: options => {
    const index = options.indexOf('em');
    return index === -1
      ? options
      : [
          ...options.slice(0, index + 1),
          'underline',
          ...options.slice(index + 1),
        ];
  },
};

const defaultTransformer = new MessageMarkdownTransformer(messageSchema);

const defaultMarkdown = {
  schema: messageSchema,
  parse: content => defaultTransformer.parse(content),
  serialize: doc => MessageMarkdownSerializer.serialize(doc),
  plugins: () => [],
  menuOptions: options => options,
};

export const editorMarkdownFor = channelType =>
  channelType === INBOX_TYPES.EMAIL ? emailMarkdown : defaultMarkdown;

// For code that holds an editor state but not its channel: a node parsed under one schema is
// rejected by an editor built on the other.
export const editorMarkdownForSchema = schema =>
  schema === underlineSchema ? emailMarkdown : defaultMarkdown;
